import { useMemoizedFn, useMount } from 'ahooks';
import { Empty, Skeleton } from 'antd';
import { useAppDispatch } from 'app/hooks';
import { Paged } from 'domains/appDomain';
import { OrderHistoryItem } from 'domains/orderDomain';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';

interface Props<T> {
  className?: string;
  loadMore: (pageNumber: number) => Promise<Paged<T>>;
  render: ({ items }: { items?: T[] }) => JSX.Element;
}

export const OrderInfiniteList = ({
  className,
  loadMore,
  render,
}: Props<OrderHistoryItem>) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [items, setItems] = useState<OrderHistoryItem[]>();
  const scrollRef = useRef<any>(null);

  const loadItems = useCallback(
    async (pageNumber: number) => {
      if (pageNumber < totalPages + 1 || totalPages === 0) {
        try {
          setLoading(true);
          const result = await loadMore(pageNumber);
          setTotalItems(result.totalElements);
          setTotalPages(result.totalPages);
          setItems((pre) => {
            if (pageNumber <= 1) {
              return result.content || [];
            }
            return uniqBy([...(pre || []), ...(result.content || [])], 'id');
          });
        } catch (e) {
          setItems([]);
        } finally {
          setLoading(false);
        }
      }
    },
    [loadMore, totalPages]
  );

  const reset = useMemoizedFn(() => {
    setLoading(false);
    setTotalItems(0);
    setTotalPages(0);
    // dispatch.workspace.setState({
    //   jobList: []
    // });
    setItems([]);
    if (scrollRef.current) {
      scrollRef.current.pageLoaded = 0;
    }
    loadItems(0);
  });

  useMount(() => {
    dispatch.order.setState({
      resetOrderHistoryList: reset,
      //   // updateJobList: updateJobItem,
    });
    // reset();
  });

  const renderDom = useMemo(() => {
    return React.createElement(render, { items });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  return (
    <div className={className} style={{ minHeight: 200, overflow: 'auto' }}>
      <InfiniteScroll
        ref={scrollRef}
        loadMore={loadItems}
        hasMore={(!items || totalItems > items.length) && !loading}
        threshold={300}
      >
        {renderDom}
      </InfiniteScroll>
      {Boolean((!items || !items?.length) && !loading) && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: 30 }} />
      )}
      {loading && (
        <div className="workspace-item">
          <Skeleton active />
        </div>
      )}
    </div>
  );
};
